import cx from 'classnames'
import NextImage from 'next/image'
import {
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { type SanityImageFragment } from '@data/sanity/queries/types/image'
import { getBasicImageLoader, getSanityImageUrl } from '../../lib/image'

type DetailsStatus = 'closed' | 'closing' | 'open' | 'opening'

export interface ItemProps {
  image: SanityImageFragment
  title: string
  subtitle?: ReactNode
  description?: ReactNode
  details?: ReactNode
  selected?: boolean
  disabled?: boolean
  error?: boolean
  onClick?: MouseEventHandler
  className?: string
}

const Item = ({
  image,
  title,
  subtitle,
  description,
  details,
  selected = false,
  disabled = false,
  error = false,
  onClick,
  className,
}: ItemProps) => {
  const detailsContentRef = useRef<HTMLDivElement>(null)
  const [detailsStatus, setDetailsStatus] = useState<DetailsStatus>(
    selected ? 'open' : 'closed',
  )

  useEffect(() => {
    setDetailsStatus((currentDetailsStatus) => {
      // Check if status does not need to change
      if (selected && currentDetailsStatus === 'open') {
        return currentDetailsStatus
      }

      if (!selected && currentDetailsStatus === 'closed') {
        return currentDetailsStatus
      }

      // Update status
      return selected ? 'opening' : 'closing'
    })
  }, [selected])

  const handleDetailsTransitionEnd = useCallback(() => {
    setDetailsStatus((currentDetailsStatus) => {
      if (currentDetailsStatus === 'opening') {
        return 'open'
      }

      if (currentDetailsStatus === 'closing') {
        return 'closed'
      }

      return currentDetailsStatus
    })
  }, [])

  const imageUrl = getSanityImageUrl(image, {
    width: 80,
    height: 80,
  })
  const openStatuses: DetailsStatus[] = ['opening', 'open']

  return (
    <div
      onClick={onClick}
      className={cx(
        'border',
        {
          'opacity-30 cursor-not-allowed': disabled,
          'cursor-auto': !disabled && selected,
          'cursor-pointer': !disabled && !selected,
          'bg-white': selected,
          'bg-gray-100': !selected,
          'border-error': error,
          'border-black': !error && selected,
          'border-gray-400': !error && !selected,
        },
        className,
      )}
    >
      <div className="flex p-2 space-x-3">
        {imageUrl && (
          <NextImage
            src={imageUrl}
            width={image.asset?.metadata?.dimensions?.width}
            height={image.asset?.metadata?.dimensions?.height}
            alt={title}
            loader={getBasicImageLoader}
            unoptimized
            className=" flex-none w-[80px] h-[80px] border border-gray-400"
          />
        )}

        <div className="flex-1 p-1">
          <h4 className="text-sm leading-5">{title}</h4>
          {subtitle && <p className="text-xs leading-4">{subtitle}</p>}
          {description && (
            <div className="flex text-xs leading-4">{description}</div>
          )}
        </div>
      </div>

      {details && (
        <div
          onTransitionEnd={handleDetailsTransitionEnd}
          className="w-full transition-all duration-200 overflow-hidden"
          style={{
            height: openStatuses.includes(detailsStatus)
              ? detailsContentRef.current?.clientHeight ?? 'auto'
              : 0,
          }}
        >
          <div ref={detailsContentRef}>{details}</div>
        </div>
      )}
    </div>
  )
}

export default Item
